import { useState } from "react";
import { ArrowRight, Headset, Mail, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO_CARDS = [
    {
        icon: Headset,
        title: "Support",
        lines: ["Need help with an exchange? Support is available through the official DtOrbit platform after you start your exchange."],
    },
    {
        icon: Mail,
        title: "Email",
        lines: ["Our official support email will be published here soon.", "Until then, please use the contact form on this page."],
    },
    {
        icon: MapPin,
        title: "Office",
        lines: ["DtOrbit currently operates digitally.", "Office details will be published here once available."],
    },
];

const inputClass =
    "w-full rounded-xl border border-[#D5E4FF] bg-white px-4 py-3 text-sm text-[#0F1B3D] placeholder:text-[#8494B0] outline-none transition-colors focus:border-[#1E56FF] focus:ring-2 focus:ring-[#1E56FF]/15";

export default function Contact() {
    usePageMeta({
        title: "Contact DtOrbit | USDT to INR Exchange Support",
        description:
            "Get in touch with the DtOrbit team. Questions about USDT to INR exchange, partnerships or support — send us a message.",
        path: "/contact",
    });

    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", message: "" });
        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Submission failed");
            setStatus({ state: "success", message: json.message || "Message sent. We will get back to you soon." });
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            setStatus({ state: "error", message: err.message || "Something went wrong. Please try again." });
        }
    };

    return (
        <>
            <PageHero
                testid="contact-hero"
                label="Contact Us"
                title="Get in"
                highlight="Touch"
                description="Questions about USDT to INR exchange, partnerships or support — send us a message and the DtOrbit team will get back to you."
            />

            <section data-testid="contact-content" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-8 lg:gap-12">
                    <div className="lg:col-span-2 flex flex-col gap-5">
                        {INFO_CARDS.map(({ icon: Icon, title, lines }, i) => (
                            <Reveal key={title} delay={i * 90}>
                                <div className="glass card-hover rounded-2xl p-6" data-testid={`contact-info-${title.toLowerCase()}`}>
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                        <Icon className="w-5 h-5 text-[#1E56FF]" />
                                    </span>
                                    <h2 className="font-display mt-4 text-lg font-bold text-[#0F1B3D]">
                                        {title}
                                    </h2>
                                    {lines.map((line) => (
                                        <p key={line} className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <div className="lg:col-span-3">
                        <Reveal delay={100}>
                            <form
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="glass rounded-2xl p-6 md:p-8 flex flex-col gap-5"
                            >
                                <h2 className="font-display text-xl md:text-2xl font-bold text-[#0F1B3D]">
                                    Send Us a Message
                                </h2>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="contact-name" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Name *</label>
                                        <input id="contact-name" name="name" required value={form.name} onChange={onChange} data-testid="contact-name-input" className={inputClass} placeholder="Your name" />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-email" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Email *</label>
                                        <input id="contact-email" name="email" type="email" required value={form.email} onChange={onChange} data-testid="contact-email-input" className={inputClass} placeholder="you@example.com" />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-phone" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Phone</label>
                                        <input id="contact-phone" name="phone" value={form.phone} onChange={onChange} data-testid="contact-phone-input" className={inputClass} placeholder="+91 …" />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-subject" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Subject *</label>
                                        <input id="contact-subject" name="subject" required value={form.subject} onChange={onChange} data-testid="contact-subject-input" className={inputClass} placeholder="What is this about?" />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="contact-message" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Message *</label>
                                    <textarea id="contact-message" name="message" rows={5} required value={form.message} onChange={onChange} data-testid="contact-message-input" className={inputClass} placeholder="Write your message…" />
                                </div>
                                {status.message && (
                                    <p
                                        data-testid="contact-form-status"
                                        className={`text-sm font-semibold ${
                                            status.state === "success" ? "text-emerald-600" : "text-rose-500"
                                        }`}
                                    >
                                        {status.message}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    disabled={status.state === "loading"}
                                    className="btn-primary self-start disabled:opacity-60 disabled:pointer-events-none"
                                >
                                    {status.state === "loading" ? "Sending…" : "Send Message"}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
